/*
The function `maybeScaleDownImage` takes in the width and height of an image, and
returns a target width and height based on certain conditions.

We scale images down, but never scale them up.

The target width and target height are interchangeable, in the sense that the
smaller of the two must be no larger than 768 and the larger of the two
must be no larger than 2000.
The aspect ratio and orientation of the original image should be preserved,
but either output dimension can be the "short" one,
and the other the "long" one.

Examples:
1. an image that is 768w by 768h would be scaled to 768w by 768h.
2. an image that is 2000w by 2000h would be scaled to 768w by 768h.
3. An image that is 1000w by 2000h would be scaled to 768w by 1536h.
4. An image that is 2000w by 1000h would be scaled to 1536w by 768h.
5. An image that is 100w by 100h would be scaled to 100w by 100h.
 */

// Exported for testing.
import {
    OPENAI_MAX_LONG_DIMENSION,
    OPENAI_MAX_SHORT_DIMENSION,
} from "../constants.ts";

export function maybeScaleDownImage(
    originalWidth: number,
    originalHeight: number,
): {
    targetWidth: number;
    targetHeight: number;
} {
    const aspectRatio = originalWidth / originalHeight;

    let targetWidth = originalWidth;
    let targetHeight = originalHeight;

    // Scale down images to fit within the constraints
    if (targetWidth > targetHeight) {
        // Landscape orientation (including wider rectangles)
        if (targetWidth > OPENAI_MAX_LONG_DIMENSION) {
            targetWidth = OPENAI_MAX_LONG_DIMENSION;
            targetHeight = Math.round(targetWidth / aspectRatio);
        }
        if (targetHeight > OPENAI_MAX_SHORT_DIMENSION) {
            targetHeight = OPENAI_MAX_SHORT_DIMENSION;
            targetWidth = Math.round(targetHeight * aspectRatio);
        }
    } else {
        // Portrait orientation (including taller rectangles and squares)
        if (targetHeight > OPENAI_MAX_LONG_DIMENSION) {
            targetHeight = OPENAI_MAX_LONG_DIMENSION;
            targetWidth = Math.round(targetHeight * aspectRatio);
        }
        if (targetWidth > OPENAI_MAX_SHORT_DIMENSION) {
            targetWidth = OPENAI_MAX_SHORT_DIMENSION;
            targetHeight = Math.round(targetWidth / aspectRatio);
        }
    }

    return {targetWidth, targetHeight};
}
